import { FormikErrors, FormikProps, getIn, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { FormEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button, IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { EnglishLettersPattern } from '@utils/formValidation'
import { isEmpty } from '@utils/isEmpty'

import { NameDto } from '@model/NameDto'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'

import { FieldGroup } from '@global/form/FieldGroup'
import { createFormDatePickerField } from '@global/formField/FieldDatePicker'
import { createFormField } from '@global/formField/FormField'
import { RawSelectFormField } from '@global/formField/FormSelectField'

import { useLinksConfig } from '@hooks/useLinksConfig'

import { Box } from '@shared/components/Box'
import { DropdownInput } from '@shared/components/Dropdown'
import { FieldWrapper } from '@shared/components/FieldWrapper'

import styles from '../CompanyDetailsStep.module.scss'

const FormField = createFormField()
const FormDatePickerField = createFormDatePickerField()

export interface CompanyDetailsStep2FormValues {
  roleInCompanyType: string
  primaryContactRoleInCompanyType: string
  legalRepresentationBasisId: number
  ownershipStructureId: number
  companyOwners: CompanyOwner[]
  numberOfOwners: number
  personResponsibleCompletingApplicationCopy: boolean
}

interface CompanyOwner {
  name: string
  ownership: string
  birthday: string
  street: string
  city: string
  state: string
  zipCode: string
  country: NameDto<string>
  taxIdentificationNumber: string
}

const CompanyDetailsStep2FormUI: React.FC<
  FormikProps<CompanyDetailsStep2FormValues> & OuterProps
> = (props) => {
  const { values, isSubmitting, isValid } = props
  const { legalBases = [], ownershipStructures = [], regulators = [] } = props
  const { handleSubmit, validateForm, setFieldValue, setSubmitting } = props

  const { t } = useTranslation()
  const isArabic = useArabicSessionLanguage()

  const handleAddNumberOfOwners = () => {
    const newOwners = [...values.companyOwners, createEmptyOwner()]
    setFieldValue('companyOwners', newOwners)
    setFieldValue('numberOfOwners', values.numberOfOwners + 1)
  }

  const handleSubmitForm: FormEventHandler = (event) => {
    setSubmitting(true)
    event.preventDefault()
    handleSubmit()
  }

  const sizeOptions = Array.from({ length: 10 }, (_, i) => i + 1).map((v) => ({
    name: v.toString(),
    id: v,
  }))

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmitForm} className={styles.form}>
        <Heading6 medium>{t('Sign up.Ownership Structure')}</Heading6>
        <FieldWrapper>
          <RawSelectFormField
            showLabel
            name='legalRepresentationBasisId'
            label={t('Sign up.Legal Representation Basis')}
            required
            options={legalBases}
            hasCheckMark={false}
            dropdownListVariant='compact'
          />
          <RawSelectFormField
            showLabel
            name='ownershipStructureId'
            label={t('Sign up.Ownership Structure')}
            required
            options={ownershipStructures}
            hasCheckMark={false}
            dropdownListVariant='compact'
          />
          <RawSelectFormField
            name='numberOfOwners'
            label={t('Sign up.Number of Owners / Shareholders')}
            required
            options={sizeOptions}
            hasCheckMark={false}
            dropdownListVariant='compact'
          />
        </FieldWrapper>
        {Array.from({ length: values.numberOfOwners }).map((_, id) => {
          return (
            <Shareholder id={id} name={`companyOwners[${[id]}]`} regulators={regulators} key={id} />
          )
        })}
        <div className='pt-4 p-0'>
          <Button.Ghost size='sm' onClick={handleAddNumberOfOwners}>
            {t('Sign up.+ Add Representative')}
          </Button.Ghost>
        </div>
        <div className={styles.button}>
          <Button.Primary
            size='lg'
            type='submit'
            fullWidth
            iconRight={isArabic ? ArrowIcon.Left : ArrowIcon.Right}
            disabled={isSubmitting || !isValid}
            onClick={() => validateForm()}
          >
            {t('Next')}
          </Button.Primary>
        </div>
      </Form>
    </React.Fragment>
  )
}

interface ShareholderProps {
  id: number
  name: string
  regulators: RegulatorOptionDto[]
}

const Shareholder: React.FC<ShareholderProps> = (props) => {
  const { id, name, regulators } = props

  const countryOptions = regulators.map((regulator) => regulator.country)
  const { t } = useTranslation()
  const { validateCity, validateState, validateStreet, validateCountry, validateZipCode } =
    useFormValidation()
  const minEighteenYearsOldDate = new Date()
  minEighteenYearsOldDate.setFullYear(minEighteenYearsOldDate.getFullYear() - 18)

  const { links } = useLinksConfig()
  const { values, setFieldValue } = useFormikContext<CompanyDetailsStep2FormValues>()
  const [countrySearch, setCountrySearch] = useState('')

  const handleRemoveNumberOfOwners = () => {
    const newOwners = values.companyOwners.filter((_, index) => index !== id)
    setFieldValue('companyOwners', newOwners)
    setFieldValue('numberOfOwners', newOwners.length)
  }
  const countryDropdownOptions = (countryOptions || []).map((c) => ({
    id: String(c.id),
    name: c.name,
  }))

  return (
    <React.Fragment>
      <Heading6 className='is-flex is-justify-content-space-between is-align-items-center' medium>
        <Box flex>
          <Body1>
            {t('Sign up.Shareholder')} {id + 1}{' '}
            <Tooltip
              content={t(
                'Sign up.Each Director, and any Shareholder who owns more than 25% of the shares must'
              )}
            >
              <ActionIcon icon={InfoIcon} size='x20' />
            </Tooltip>
          </Body1>
        </Box>{' '}
        {id >= 1 && (
          <Body1 className='is-flex is-align-items-center'>
            <IconButton.Ghost icon={CloseIcon} onClick={handleRemoveNumberOfOwners} />
          </Body1>
        )}
      </Heading6>
      <FieldWrapper>
        <FormField
          name={`${name}.name`}
          label={t('Sign up.Name')}
          type='text'
          max={100}
          min={1}
          required
          showLabel
          pattern='.*\S+.*'
        />
        <FormDatePickerField
          name={`${name}.birthday`}
          label={t('Sign up.Date of Birth')}
          required
          showLabel
          showYearDropdown
          maxDate={minEighteenYearsOldDate}
        />
        <DropdownInput
          showLabel
          name={`${name}.country`}
          value={getIn(values, `${name}.country.id`)}
          label={t('Sign up.Country of Residence')}
          required
          validate={validateCountry}
          options={countryDropdownOptions}
          hasCheckMark={false}
          searchInput={{
            value: countrySearch,
            handleChange: (e) => setCountrySearch(e.target.value),
            handleClear: () => setCountrySearch(''),
          }}
          onChange={(option) => {
            setFieldValue(`${name}.country`, option)
            setCountrySearch('')
          }}
        />
        <FormField
          name={`${name}.ownership`}
          label={t('Sign up.Ownership (%)')}
          type='number'
          min='0'
          required
          showLabel
        />
        <FormField
          name={`${name}.street`}
          label={t('Sign up.Street Name, Street Number, Apt. Number')}
          validate={validateStreet}
          required
          showLabel
          pattern='.*\S+.*'
        />
        <FormField
          name={`${name}.city`}
          label={t('Sign up.City')}
          validate={validateCity}
          required
          showLabel
          pattern='.*\S+.*'
        />
        <FieldGroup>
          <FormField
            name={`${name}.state`}
            label={t('Sign up.State/Region')}
            validate={validateState}
            required
            showLabel
            pattern='.*\S+.*'
          />
          <FormField
            name={`${name}.zipCode`}
            label={t('Sign up.ZIP/Postal Code')}
            type='text'
            validate={validateZipCode}
            required
            showLabel
            pattern='.*\S+.*'
          />
        </FieldGroup>
      </FieldWrapper>
      <Heading6 className='is-flex is-align-items-center' medium>
        {t('Sign up.Tax Identification Number (TIN) / Goods and Services Tax Number (GSTN)')}{' '}
        <Tooltip
          content={
            <Body1
              isParagraph
              dangerouslySetInnerHTML={{
                __html: t("Sign up.Here we'll need your individual Tax Identification Number", {
                  eu_link: links.TIN.EuropeanCommission,
                  worldwide_link: links.TIN.OECD,
                }),
              }}
            />
          }
        >
          <ActionIcon icon={InfoIcon} size='x20' />
        </Tooltip>
      </Heading6>
      <FormField
        name={`${name}.taxIdentificationNumber`}
        label={t('Sign up.Tax Identification Number (TIN) / Goods and Services Tax Number (GSTN)')}
        type='text'
        showLabel
      />
    </React.Fragment>
  )
}

const useFormValidation = () => {
  const { t } = useTranslation()

  return createFormValidators(t)
}

const createFormValidators = (getTranslation: (key: string) => string) => {
  const validateStreet = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid street')}`
    }
    return undefined
  }

  const validateCity = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid city')}`
    }
  }

  const validateState = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid state')}`
    }
  }

  const validateZipCode = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (value.length > 32) {
      return `${getTranslation('Validation.Maximum character limit is 32')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid postal code')}`
    }
  }

  const validateCountry = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
  }

  return { validateStreet, validateCity, validateState, validateZipCode, validateCountry }
}

interface OuterProps {
  legalBases: NameDto[]
  ownershipStructures: NameDto[]
  regulators: RegulatorOptionDto[]
  initialValues: CompanyDetailsStep2FormValues | undefined

  onSubmit(values: CompanyDetailsStep2FormValues): void
}

const createEmptyOwner = (): CompanyOwner => ({
  name: '',
  ownership: '',
  birthday: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: { id: '', name: '' },
  taxIdentificationNumber: '',
})

const createCompanyOwnersForCount = (
  owners: CompanyOwner[] = [],
  numberOfOwners: number
): CompanyOwner[] => {
  const ownerCount = Math.max(Number(numberOfOwners) || 0, 0)

  return Array.from({ length: ownerCount }, (_, id) => owners[id] || createEmptyOwner())
}

export const CompanyDetailsStep2Form = withFormik<OuterProps, CompanyDetailsStep2FormValues>({
  mapPropsToValues: ({ initialValues }) => {
    const numOwners = initialValues?.numberOfOwners || 1

    return {
      ...initialValues,
      roleInCompanyType: initialValues?.roleInCompanyType || '',
      legalRepresentationBasisId: initialValues?.legalRepresentationBasisId ?? 0,
      ownershipStructureId: initialValues?.ownershipStructureId ?? 0,
      primaryContactRoleInCompanyType: initialValues?.primaryContactRoleInCompanyType || '',
      numberOfOwners: numOwners,
      companyOwners: createCompanyOwnersForCount(initialValues?.companyOwners, numOwners),
      personResponsibleCompletingApplicationCopy:
        initialValues?.personResponsibleCompletingApplicationCopy || true,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit({
        ...values,
        companyOwners: values.companyOwners.filter((x) => !!x),
        primaryContactRoleInCompanyType: values.personResponsibleCompletingApplicationCopy
          ? values.roleInCompanyType
          : values.primaryContactRoleInCompanyType,
      })
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values: CompanyDetailsStep2FormValues) => {
    const errors: FormikErrors<CompanyDetailsStep2FormValues> = {}
    const required = t('Validation.Required')

    const formValidators = createFormValidators(t)

    if (!values.legalRepresentationBasisId) {
      errors.legalRepresentationBasisId = required
    }

    if (!values.ownershipStructureId) {
      errors.ownershipStructureId = required
    }

    if (!values.numberOfOwners || values.numberOfOwners < 1) {
      errors.numberOfOwners = required
    }

    if (values.numberOfOwners > 0) {
      const companyOwners = createCompanyOwnersForCount(
        values.companyOwners,
        values.numberOfOwners
      ).map((owner) => {
        const ownerErrors: FormikErrors<CompanyOwner> = {}

        if (!owner.name?.trim()) {
          ownerErrors.name = required
        }

        if (!owner.birthday) {
          ownerErrors.birthday = required
        }

        const countryError = formValidators.validateCountry(owner.country?.id || '')
        if (countryError) {
          ownerErrors.country = countryError as FormikErrors<NameDto<string>>
        }

        if (!owner.ownership?.trim()) {
          ownerErrors.ownership = required
        }

        const streetError = formValidators.validateStreet(owner.street || '')
        if (streetError) ownerErrors.street = streetError

        const cityError = formValidators.validateCity(owner.city || '')
        if (cityError) ownerErrors.city = cityError

        const stateError = formValidators.validateState(owner.state || '')
        if (stateError) ownerErrors.state = stateError

        const zipError = formValidators.validateZipCode(owner.zipCode || '')
        if (zipError) ownerErrors.zipCode = zipError

        return Object.keys(ownerErrors).length > 0 ? ownerErrors : undefined
      })

      if (companyOwners.some(Boolean)) {
        errors.companyOwners = companyOwners as FormikErrors<CompanyOwner>[]
      }
    }

    return errors
  },
})(CompanyDetailsStep2FormUI)
